/**
 * condition-row.tsx — single condition row in the alarm builder.
 *
 * Layout: [field select] [operator select] [value input] [remove]
 *
 * Operator list comes from catalog field.operators — never hardcoded.
 * Value input adapts to field.kind:
 *   number → <input type="number">
 *   bool   → toggle buttons (true / false)
 *   string → <input type="text">
 *   $in/$nin (any kind) → tag-input (comma-separated chips)
 */
import { X } from 'lucide-react';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Input,
  IconButton,
  SegmentedControl,
} from '@vnetwork/ui/primitives';
import type { CatalogField } from '@/api/healthcheck';
import type { ConditionRow as RowValue } from './condition-serializer';
import { coerceValue } from './condition-serializer';
import styles from './ConditionRow.module.css';

interface Props {
  row: RowValue;
  fields: CatalogField[];
  onChange: (patch: Partial<RowValue>) => void;
  onRemove: () => void;
  canRemove: boolean;
  showError?: boolean;
}

const OPERATOR_LABELS: Record<string, string> = {
  $eq: '=',
  $neq: '≠',
  $gt: '>',
  $gte: '≥',
  $lt: '<',
  $lte: '≤',
  $in: 'in',
  $nin: 'not in',
  $contains: 'contains',
  $prefix: 'starts with',
  $matches: 'matches',
  $sigma: 'σ anomaly (spike only)',
  $sigma_abs: 'σ anomaly (spike+drop)',
  $sigma_drop: 'σ anomaly (drop only)',
};

function opLabel(op: string) {
  return OPERATOR_LABELS[op] ?? op;
}

/** Friendly labels for derived concentration metrics (backend names are prefixed). */
const FIELD_LABELS: Record<string, string> = {
  'derived:top1_count': 'top source concentration',
  'derived:distinct_count': 'distinct sources',
};

function fieldLabel(name: string) {
  return FIELD_LABELS[name] ?? name;
}

function isMultiOp(op: string) {
  return op === '$in' || op === '$nin';
}

function isSigmaOp(op: string) {
  return op === '$sigma' || op === '$sigma_abs' || op === '$sigma_drop';
}

/** Default σ multiplier when switching to the $sigma operator (mean + 3σ). */
const DEFAULT_SIGMA_K = 3;

export function ConditionRow({ row, fields, onChange, onRemove, canRemove, showError }: Props) {
  const selectedField = fields.find((f) => f.name === row.field) ?? null;
  const operators = selectedField?.operators ?? [];
  const kind = selectedField?.kind ?? 'string';
  const multi = isMultiOp(row.operator);

  const handleFieldChange = (fieldName: string) => {
    const f = fields.find((ff) => ff.name === fieldName);
    const firstOp = f?.operators[0] ?? '';
    // Reset operator + value when field changes
    onChange({ field: fieldName, operator: firstOp, value: '' });
  };

  const handleOperatorChange = (op: string) => {
    // Switching to $sigma from a fixed-threshold op: the value's meaning changes
    // (absolute threshold → σ multiplier k), so seed a sensible default.
    if (isSigmaOp(op) && !isSigmaOp(row.operator)) {
      onChange({ operator: op, value: DEFAULT_SIGMA_K });
      return;
    }
    // Coerce existing value to new op shape
    const newValue = coerceValue(
      Array.isArray(row.value) ? (row.value as unknown[]).join(', ') : row.value,
      kind,
      op,
    );
    onChange({ operator: op, value: newValue });
  };

  const handleValueChange = (raw: unknown) => {
    onChange({ value: coerceValue(raw, kind, row.operator) });
  };

  const rowError = showError && (!row.field || !row.operator || row.value === '' || row.value === null || row.value === undefined || (Array.isArray(row.value) && row.value.length === 0));

  return (
    <div className={styles.conditionRow}>
      {/* Field select */}
      <Select value={row.field} onValueChange={handleFieldChange}>
        <SelectTrigger style={{ width: '100%' }} invalid={showError && !row.field}>
          <SelectValue placeholder="Choose field…" />
        </SelectTrigger>
        <SelectContent>
          {fields.map((f) => (
            <SelectItem key={f.name} value={f.name}>
              {fieldLabel(f.name)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Operator select — constrained to selected field's operators */}
      <Select
        value={row.operator}
        onValueChange={handleOperatorChange}
        disabled={!selectedField}
      >
        <SelectTrigger style={{ width: '100%' }} invalid={showError && !row.operator}>
          <SelectValue placeholder="op" />
        </SelectTrigger>
        <SelectContent>
          {operators.map((op) => (
            <SelectItem key={op} value={op}>
              {opLabel(op)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Value input — adapts to kind + operator */}
      <div>
        {kind === 'bool' && !multi ? (
          <BoolToggle
            value={row.value as boolean}
            onChange={(v) => handleValueChange(v)}
            invalid={rowError}
          />
        ) : multi ? (
          <TagInput
            values={Array.isArray(row.value) ? (row.value as string[]) : []}
            kind={kind}
            onChange={(arr) => onChange({ value: arr })}
            invalid={rowError}
          />
        ) : (
          <Input
            type={kind === 'number' ? 'number' : 'text'}
            value={row.value === undefined || row.value === null ? '' : String(row.value)}
            onChange={(e) => handleValueChange(e.target.value)}
            placeholder="value"
            disabled={!selectedField || !row.operator}
            invalid={rowError}
            style={{ width: '100%' }}
          />
        )}
        {isSigmaOp(row.operator) && !rowError && (
          <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-xs)' }}>
            {row.operator === '$sigma_abs'
              ? 'σ multiplier — fires when |value − mean| ≥ k·stddev of the 7-day baseline (spike or drop).'
              : row.operator === '$sigma_drop'
                ? 'σ multiplier — fires when value < mean − k·stddev of the 7-day baseline (drop only).'
                : 'σ multiplier — fires when value > mean + k·stddev of the 7-day baseline (spike only).'}
          </span>
        )}
        {rowError && (
          <span style={{ color: 'var(--vn-danger-text)', fontSize: 'var(--vn-font-size-xs)' }}>
            Required
          </span>
        )}
      </div>

      {/* Remove */}
      <IconButton
        variant="ghost"
        size="sm"
        aria-label="Remove condition"
        onClick={onRemove}
        disabled={!canRemove}
        style={{ marginTop: 4 }}
      >
        <X size={14} />
      </IconButton>
    </div>
  );
}

// ---- Sub-components ------------------------------------------------------

const BOOL_ITEMS = [
  { value: 'true', label: 'true' },
  { value: 'false', label: 'false' },
];

function BoolToggle({
  value,
  onChange,
  invalid: _invalid,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
  /** Reserved for future error state wiring; SegmentedControl handles its own selection state */
  invalid?: boolean;
}) {
  return (
    <SegmentedControl
      size="sm"
      items={BOOL_ITEMS}
      value={value === true ? 'true' : 'false'}
      onValueChange={(v) => onChange(v === 'true')}
    />
  );
}

function TagInput({
  values,
  kind,
  onChange,
  invalid,
}: {
  values: string[];
  kind: 'string' | 'number' | 'bool';
  onChange: (arr: unknown[]) => void;
  invalid?: boolean;
}) {
  const coercedValues = values.map((v) => String(v));

  const addTag = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    const coerced = kind === 'number' ? parseFloat(trimmed) : trimmed;
    onChange([...values, coerced]);
  };

  const removeTag = (i: number) => {
    onChange(values.filter((_, idx) => idx !== i));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag((e.target as HTMLInputElement).value);
      (e.target as HTMLInputElement).value = '';
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value.trim()) {
      addTag(e.target.value);
      e.target.value = '';
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        padding: '4px 8px',
        border: `1px solid ${invalid && values.length === 0 ? 'var(--vn-danger-text)' : 'var(--vn-border-default)'}`,
        borderRadius: 'var(--vn-radius-md)',
        background: 'var(--vn-bg-surface)',
        minHeight: 36,
        alignItems: 'center',
      }}
    >
      {coercedValues.map((v, i) => (
        <span
          key={i}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
            padding: '1px 6px',
            background: 'var(--vn-bg-hover)',
            borderRadius: 'var(--vn-radius-sm)',
            fontSize: 'var(--vn-font-size-xs)',
          }}
        >
          {v}
          <button
            type="button"
            onClick={() => removeTag(i)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: 'var(--vn-fg-muted)' }}
          >
            ×
          </button>
        </span>
      ))}
      <input
        type={kind === 'number' ? 'number' : 'text'}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={values.length === 0 ? 'value, Enter to add' : 'add…'}
        style={{
          border: 'none',
          outline: 'none',
          background: 'transparent',
          font: 'inherit',
          fontSize: 'var(--vn-font-size-sm)',
          minWidth: 80,
          flex: 1,
        }}
      />
    </div>
  );
}
